import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { rolePageAccessApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

export interface PageMeta {
  key: string;
  label: string;
  group: string;
}

interface PermissionContextValue {
  role: string | null;
  access: Record<string, boolean>;
  pages: PageMeta[];
  loading: boolean;
  can: (page: string) => boolean;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [role, setRole] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<string, boolean>>({});
  const [pages, setPages] = useState<PageMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setRole(null);
      setAccess({});
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res: any = await rolePageAccessApi.me();
      const payload = res.data?.data || res.data;
      setRole(payload?.role || user.role || null);
      setAccess(payload?.access || {});
      setPages(payload?.pages || []);
    } catch (err) {
      console.error("Failed to load permissions:", err);
      setRole(user.role || null);
      setAccess({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (page: string) => {
      // super_admin always passes even if API hiccups
      if (role === "super_admin") return true;
      return access[page] === true;
    },
    [access, role]
  );

  return (
    <PermissionContext.Provider value={{ role, access, pages, loading, can, refresh: load }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermission must be used inside <PermissionProvider>");
  }
  return ctx;
}
